import React from 'react';
import { useLocation, useParams, Link } from 'react-router-dom';
import { ChevronRight, Home, BookOpen, FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ALL_COURSE_CATEGORIES } from '@/config/courseNavigation';

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: React.ComponentType<any>;
  active?: boolean;
  badge?: string;
}

const EnhancedBreadcrumbNavigation: React.FC = () => {
  const location = useLocation();
  const params = useParams();
  const { categoryId, courseId, moduleId } = params;

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const breadcrumbs: BreadcrumbItem[] = [
      {
        label: 'Home',
        href: '/',
        icon: Home
      }
    ];

    // Handle course category pages
    if (location.pathname.includes('-courses') && categoryId) {
      const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
      if (category) {
        breadcrumbs.push({
          label: 'Courses',
          href: '/courses',
          icon: BookOpen
        });
        breadcrumbs.push({
          label: category.title,
          href: `/${categoryId}-courses`,
          icon: category.icon,
          badge: `${category.courses.length} courses`
        });
      }
    }

    // Handle specific course pages
    if (courseId && categoryId) {
      const category = ALL_COURSE_CATEGORIES.find(cat => cat.id === categoryId);
      const course = category?.courses.find(c => c.id === courseId);
      
      if (category && course) {
        // Add category if not already added
        if (!breadcrumbs.some(b => b.href?.includes(`${categoryId}-courses`))) {
          breadcrumbs.push({
            label: 'Courses',
            href: '/courses',
            icon: BookOpen
          });
          breadcrumbs.push({
            label: category.title,
            href: `/${categoryId}-courses`,
            icon: category.icon
          });
        }

        breadcrumbs.push({
          label: course.title,
          href: course.route,
          badge: course.level
        });
      }
    }

    // Handle module pages
    if (moduleId) {
      breadcrumbs.push({
        label: `Module ${moduleId}`,
        icon: FileText,
        active: true
      });
    }

    // Handle other common routes
    if (location.pathname === '/dashboard') {
      breadcrumbs.push({
        label: 'Dashboard',
        active: true
      });
    }

    if (location.pathname === '/courses') {
      breadcrumbs.push({
        label: 'All Courses',
        icon: BookOpen,
        active: true,
        badge: `${ALL_COURSE_CATEGORIES.length} categories`
      });
    }

    if (location.pathname === '/admin') {
      breadcrumbs.push({
        label: 'Admin Portal',
        active: true
      });
    }

    if (location.pathname === '/staff-dashboard') {
      breadcrumbs.push({
        label: 'Staff Portal',
        active: true
      });
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page or if there's only one item
  if (location.pathname === '/' || breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav 
      className="flex items-center space-x-1 text-sm text-muted-foreground py-4 px-6 bg-muted/30 border-b"
      aria-label="Breadcrumb"
    >
      <ol className="flex items-center space-x-1 md:space-x-2">
        {breadcrumbs.map((item, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const IconComponent = item.icon;

          return (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground/50" />
              )}
              
              <div className="flex items-center gap-2">
                {item.href && !isLast ? (
                  <Link 
                    to={item.href}
                    className="flex items-center gap-2 hover:text-foreground transition-colors"
                  >
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="sm:hidden">
                      {IconComponent ? <IconComponent className="h-4 w-4" /> : item.label}
                    </span>
                    {item.badge && (
                      <Badge variant="outline" className="text-xs">
                        {item.badge}
                      </Badge>
                    )}
                  </Link>
                ) : (
                  <div className={`flex items-center gap-2 ${isLast ? 'text-foreground font-medium' : ''}`}>
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    <span className="hidden sm:inline">{item.label}</span>
                    <span className="sm:hidden">
                      {IconComponent ? <IconComponent className="h-4 w-4" /> : item.label}
                    </span>
                    {item.badge && (
                      <Badge variant="outline" className="text-xs">
                        {item.badge}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {/* Quick Actions */}
      <div className="flex-1 flex justify-end gap-2">
        {location.pathname.includes('/course/') && (
          <Button variant="ghost" size="sm" className="gap-2">
            <BookOpen className="h-3 w-3" />
            Course Menu
          </Button>
        )}
      </div>
    </nav>
  );
};

export default EnhancedBreadcrumbNavigation;